"use client"

import { useState, useTransition, useEffect } from "react"
import {
  Modal, Stack, Text, Group, Badge, NumberInput, Button, Alert, Divider, Center, Loader,
} from "@forestsync/ui"
import { TbAlertTriangle, TbCheck } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import Link from "next/link"
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { prepareBrowseBuy, buyCreditsFromBrowse } from "@/actions/browse-buy"
import { cancelTradePaymentIntent, getTraderBalance, createTraderBalancePaymentIntent } from "@/actions/trade-payment"
import { getCountryName } from "@forestsync/db"
import type { Sink } from "@/components/shared/ProjectCard"

// Load Stripe.js once at module level (Stripe requirement)
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!)

interface BuyCreditsModalProps {
  sink: Sink | null
  vintage: number
  creditType: string
  onClose: () => void
  initialVolume?: number
  skipEntry?: boolean
}

type Stage = "entry" | "loading" | "payment" | "success"

type PaymentData = {
  clientSecret: string
  paymentIntentId: string
  limitPrice: number
  avgPrice: number
  subtotal: number
  platformFee: number
  preAuthAmount: number
  creditType: string
  vintage: number
}

interface BuyPaymentFormProps {
  paymentData: PaymentData
  refId: string
  volume: number
  onSuccess: (orderRef: string, filledVolume: number) => void
  onBack: () => void
  canGoBack?: boolean
}

function BuyPaymentForm({ paymentData, refId, volume, onSuccess, onBack, canGoBack = true }: BuyPaymentFormProps) {
  const t = useTranslations("browse")
  const stripe = useStripe()
  const elements = useElements()
  const [isPending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)

  function handleConfirmPay() {
    if (!stripe || !elements) return
    setError(null)
    startTransition(async () => {
      const confirmResult = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: "if_required",
      })
      if (confirmResult.error) {
        setError(confirmResult.error.message ?? t("buyError"))
        return
      }
      const piId = confirmResult.paymentIntent?.id ?? paymentData.paymentIntentId
      const result = await buyCreditsFromBrowse({
        projectRefId: refId,
        volume,
        paymentIntentId: piId,
        limitPrice: paymentData.limitPrice,
        creditType: paymentData.creditType,
        vintage: paymentData.vintage,
      })
      if ("error" in result) {
        setError(result.error ?? t("buyError"))
        return
      }
      onSuccess(result.orderRef, result.filledVolume)
    })
  }

  return (
    <>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{t("pricePerCredit")}</Text>
        <Text size="sm" fw={500}>€{paymentData.avgPrice.toFixed(2)}</Text>
      </Group>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{t("totalPrice")}</Text>
        <Text size="sm" fw={500}>€{paymentData.subtotal.toFixed(2)}</Text>
      </Group>
      {paymentData.platformFee > 0 && (
        <Group justify="space-between">
          <Group gap={4}>
            <Text size="sm" c="dimmed">{t("platformFee")}</Text>
            {paymentData.subtotal > 0 && (
              <Text size="xs" c="dimmed">({(paymentData.platformFee / paymentData.subtotal * 100).toFixed(1)}%)</Text>
            )}
          </Group>
          <Text size="sm" fw={500}>€{paymentData.platformFee.toFixed(2)}</Text>
        </Group>
      )}
      <Divider />
      <Group justify="space-between">
        <Text fw={500}>{t("preAuthorized")}</Text>
        <Text fw={600}>€{paymentData.preAuthAmount.toFixed(2)}</Text>
      </Group>
      <Text size="xs" c="dimmed">{t("estCostNote")}</Text>
      <PaymentElement />
      {error && (
        <Alert color="red" onClose={() => setError(null)} withCloseButton>
          {error}
        </Alert>
      )}
      <Group grow={canGoBack}>
        {canGoBack && (
          <Button variant="default" onClick={onBack} disabled={isPending}>
            {t("back")}
          </Button>
        )}
        <Button fullWidth={!canGoBack} color="green" autoContrast loading={isPending} onClick={handleConfirmPay}>
          {t("confirmAndPay")}
        </Button>
      </Group>
    </>
  )
}

export function BuyCreditsModal({ sink, vintage, creditType, onClose, initialVolume, skipEntry }: BuyCreditsModalProps) {
  const t = useTranslations("browse")
  const [isPending, startTransition] = useTransition()
  const [stage, setStage] = useState<Stage>(skipEntry ? "loading" : "entry")
  const [volume, setVolume] = useState<number | string>(initialVolume ?? "")
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [orderRef, setOrderRef] = useState<string | null>(null)
  const [filledVolume, setFilledVolume] = useState<number | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<"card" | "balance">("card")
  const [balanceCents, setBalanceCents] = useState<number | null>(null)

  const qty = typeof volume === "number" ? volume : 0
  const maxQty = Math.floor(sink?.askVolume ?? 0)
  const bestAsk = sink?.bestAsk ?? sink?.lastPrice ?? null
  const sinkOpen = !!sink
  // True when the server-quoted price differs from what was shown on the page
  const priceMovedDelta =
    paymentData && bestAsk !== null
      ? Math.abs(paymentData.limitPrice - bestAsk)
      : 0
  const priceMoved = priceMovedDelta > 0.005

  // When skipEntry=true, fire prepareBrowseBuy + balance check immediately when the modal opens
  useEffect(() => {
    if (!skipEntry || !sink || !initialVolume || initialVolume <= 0) return
    setStage("loading")
    setError(null)
    setPaymentData(null)
    setBalanceCents(null)
    setPaymentMethod("card")
    startTransition(async () => {
      const [result, balanceResult] = await Promise.all([
        prepareBrowseBuy({ projectRefId: sink.refId, volume: initialVolume, vintage, creditType }),
        getTraderBalance(),
      ])
      if ("error" in result) {
        const msg = result.error === "NO_SELLERS" ? t("noSellers")
          : result.error === "INSUFFICIENT_CREDITS" ? t("insufficientCredits")
          : (result.error ?? t("buyError"))
        setError(msg)
        setStage("entry")
        return
      }
      if ("balanceCents" in balanceResult) {
        setBalanceCents(balanceResult.balanceCents)
        if (balanceResult.balanceCents / 100 >= result.preAuthAmount) {
          setPaymentMethod("balance")
        }
      }
      setPaymentData(result)
      setStage("payment")
    })
  // Re-triggers when modal opens (sinkOpen goes false→true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinkOpen])

  if (!sink) return null

  // Always derive quantity from current state — fixes bug where skipEntry bypassed user edits in error recovery
  const qty2 = qty > 0 ? qty : (initialVolume ?? 0)

  function handleContinue() {
    if (qty <= 0) {
      setError(t("volumeRequired"))
      return
    }
    setError(null)
    setBalanceCents(null)
    setPaymentMethod("card")

    startTransition(async () => {
      const [result, balanceResult] = await Promise.all([
        prepareBrowseBuy({ projectRefId: sink!.refId, volume: qty, vintage, creditType }),
        getTraderBalance(),
      ])
      if ("error" in result) {
        const msg = result.error === "NO_SELLERS" ? t("noSellers")
          : result.error === "INSUFFICIENT_CREDITS" ? t("insufficientCredits")
          : (result.error ?? t("buyError"))
        setError(msg)
        return
      }
      if ("balanceCents" in balanceResult) {
        setBalanceCents(balanceResult.balanceCents)
        if (balanceResult.balanceCents / 100 >= result.preAuthAmount) {
          setPaymentMethod("balance")
        }
      }
      setPaymentData(result)
      setStage("payment")
    })
  }

  function handleBalanceConfirm() {
    if (!sink || !paymentData) return
    setError(null)
    startTransition(async () => {
      if (paymentData.paymentIntentId) {
        await cancelTradePaymentIntent({ paymentIntentId: paymentData.paymentIntentId }).catch(() => null)
      }
      const balancePi = await createTraderBalancePaymentIntent({ amountEur: paymentData.preAuthAmount })
      if ("error" in balancePi) {
        setError(balancePi.error ?? t("buyError"))
        return
      }
      const result = await buyCreditsFromBrowse({
        projectRefId: sink.refId,
        volume: qty2,
        paymentIntentId: balancePi.paymentIntentId,
        limitPrice: paymentData.limitPrice,
        creditType: paymentData.creditType,
        vintage: paymentData.vintage,
      })
      if ("error" in result) {
        setError(result.error ?? t("buyError"))
        return
      }
      setOrderRef(result.orderRef)
      setFilledVolume(result.filledVolume)
      setStage("success")
    })
  }

  function handleBack() {
    if (paymentData?.paymentIntentId) {
      cancelTradePaymentIntent({ paymentIntentId: paymentData.paymentIntentId }).catch(() => null)
    }
    setPaymentData(null)
    setStage("entry")
    setError(null)
  }

  function handleClose() {
    if ((stage === "payment" || stage === "loading") && paymentData?.paymentIntentId) {
      cancelTradePaymentIntent({ paymentIntentId: paymentData.paymentIntentId }).catch(() => null)
    }
    setVolume(initialVolume ?? "")
    setError(null)
    setStage(skipEntry ? "loading" : "entry")
    setPaymentData(null)
    setOrderRef(null)
    setFilledVolume(null)
    onClose()
  }

  return (
    <Modal opened={!!sink} onClose={handleClose} title={t("buyNow")} centered>
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600}>{sink.name}</Text>
          {sink.methodology && <Badge variant="light">{sink.methodology}</Badge>}
        </Group>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">{sink.owner?.name}</Text>
          <Text size="sm" c="dimmed">{sink.region}, {getCountryName(sink.country)}</Text>
        </Group>

        <Divider />

        {stage === "loading" && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">{t("preparingPayment")}</Text>
              {error && (
                <Alert color="red" onClose={() => setError(null)} withCloseButton mt="sm">
                  {error}
                </Alert>
              )}
            </Stack>
          </Center>
        )}

        {stage === "entry" && (
          <>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">{t("availableCredits")}</Text>
              <Text size="sm" fw={500}>{maxQty.toLocaleString()} tCO₂e</Text>
            </Group>

            {bestAsk !== null && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">{t("bestAsk")}</Text>
                <Text size="sm" fw={500}>€{bestAsk.toFixed(2)}</Text>
              </Group>
            )}

            <NumberInput
              label={t("volume")}
              placeholder="0"
              value={volume}
              onChange={setVolume}
              min={1}
              max={maxQty}
              step={1}
              decimalScale={0}
            />

            {qty > 0 && bestAsk !== null && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">{t("indicativeTotal")}</Text>
                <Text size="sm" fw={500}>~€{(qty * bestAsk).toFixed(2)}</Text>
              </Group>
            )}

            {error && (
              <Alert color="red" onClose={() => setError(null)} withCloseButton>
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              color="green"
              autoContrast
              loading={isPending}
              onClick={handleContinue}
              disabled={maxQty === 0}
            >
              {t("continueToPayment")}
            </Button>
          </>
        )}

        {stage === "payment" && paymentData && (
          <>
            {/* Payment method selector — only shown when user has sufficient balance */}
            {balanceCents !== null && balanceCents > 0 && (
              <>
                <Divider label={t("paymentMethodLabel")} labelPosition="center" />
                <Group grow>
                  <Button
                    variant={paymentMethod === "balance" ? "filled" : "light"}
                    color="teal"
                    autoContrast
                    disabled={balanceCents / 100 < paymentData.preAuthAmount}
                    onClick={() => setPaymentMethod("balance")}
                  >
                    {t("paymentBalance")} · €{(balanceCents / 100).toFixed(2)}
                  </Button>
                  <Button
                    variant={paymentMethod === "card" ? "filled" : "light"}
                    color="blue"
                    autoContrast
                    onClick={() => setPaymentMethod("card")}
                  >
                    {t("paymentCard")}
                  </Button>
                </Group>
              </>
            )}

            {/* Price-moved warning — shown when server price differs from page price */}
            {priceMoved && paymentData && (
              <Alert color="yellow" icon={<TbAlertTriangle size={16} />} title={t("priceMoved")}>
                {t("priceMovedDesc", {
                  newPrice: paymentData.limitPrice.toFixed(2),
                  oldPrice: bestAsk!.toFixed(2),
                })}
              </Alert>
            )}

            {paymentMethod === "card" ? (
              <Elements stripe={stripePromise} options={{ clientSecret: paymentData.clientSecret }}>
                <BuyPaymentForm
                  paymentData={paymentData}
                  refId={sink.refId}
                  volume={qty2}
                  canGoBack={!skipEntry}
                  onBack={handleBack}
                  onSuccess={(ref, vol) => {
                    setOrderRef(ref)
                    setFilledVolume(vol)
                    setStage("success")
                  }}
                />
              </Elements>
            ) : (
              <>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">{t("preAuthorized")}</Text>
                  <Text size="sm" fw={500}>€{paymentData.preAuthAmount.toFixed(2)}</Text>
                </Group>
                {error && (
                  <Alert color="red" onClose={() => setError(null)} withCloseButton>{error}</Alert>
                )}
                <Group grow={!skipEntry}>
                  {!skipEntry && (
                    <Button variant="default" onClick={handleBack} disabled={isPending}>
                      {t("back")}
                    </Button>
                  )}
                  <Button
                    fullWidth={!!skipEntry}
                    color="green"
                    autoContrast
                    loading={isPending}
                    onClick={handleBalanceConfirm}
                    disabled={balanceCents !== null && balanceCents / 100 < paymentData.preAuthAmount}
                  >
                    {t("confirmAndPay")}
                  </Button>
                </Group>
              </>
            )}
          </>
        )}

        {stage === "success" && (
          <>
            <Alert color="green" icon={<TbCheck size={16} />}>
              {filledVolume && filledVolume > 0
                ? t("orderConfirmed", { volume: filledVolume })
                : t("orderPending")
              }
            </Alert>
            {filledVolume && filledVolume > 0 && (
              <Text size="sm" c="dimmed">{t("creditsInPortfolio")}</Text>
            )}
            {orderRef && (
              <Text size="xs" c="dimmed">{t("orderRef")}: {orderRef}</Text>
            )}
            <Group grow>
              {filledVolume && filledVolume > 0 && (
                <Button
                  component={Link}
                  href="/dashboard/credits"
                  color="green"
                  autoContrast
                  onClick={handleClose}
                >
                  {t("viewPortfolio")}
                </Button>
              )}
              {orderRef && filledVolume && filledVolume > 0 && (
                <Button
                  variant="default"
                  component="a"
                  href={`/api/certificates/purchase?orderRef=${orderRef}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("downloadReceipt")}
                </Button>
              )}
              {(!filledVolume || filledVolume === 0) && (
                <Button variant="default" onClick={handleClose}>
                  {t("close")}
                </Button>
              )}
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
}

